import * as React from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useAddPOItemsBatch } from '@/hooks/usePOMutations'
import { useAllPOs, usePO } from '@/hooks/usePOs'
import type { components } from '@/lib/api/generated'
import { fmtDate } from '@/lib/dates'
import { useUIStore, type POLineItemPrefill } from '@/store/uiStore'
import { StatusBadge } from './StatusBadge'

type POLineItemCreate = components['schemas']['POLineItemCreate']

// A PO can only take new lines while it is still pre-order — once it's been
// sent to the vendor the line items are what was actually ordered. Mirrors the
// backend's 409 so the list never offers a PO the API would reject.
const OPEN_STATUSES = ['DRAFT', 'AWAITING_APPROVAL', 'APPROVED', 'REJECTED']

function fmtCurrency(n: number | null | undefined): string {
  if (n == null) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
}

// Same mapping the create-PO flow uses (CreatePOModal's toPOLineItem), minus
// the display-only fields the wizard carries — the API takes these eleven.
function toPOLineItem(p: POLineItemPrefill): POLineItemCreate {
  return {
    variant_id: p.variant_id,
    product_id: p.product_id,
    product_title: p.product_title,
    variant_title: p.variant_title ?? '',
    sku: p.sku ?? '',
    barcode: p.barcode ?? '',
    vendor: p.vendor,
    retail_price: 0,
    cost_base: p.unit_cost ?? 0,
    qty_ordered: p.recommended_qty || 1,
    notes: '',
  }
}

function AddToPOBody({
  onAdded,
  close,
}: {
  onAdded?: () => void
  close: () => void
}) {
  const prefillItems = useUIStore((s) => s.prefillItems)
  const [poId, setPoId] = React.useState<string | null>(null)
  const [search, setSearch] = React.useState('')

  const { data: pos = [], isLoading } = useAllPOs()
  const { data: poData } = usePO(poId)
  const addItems = useAddPOItemsBatch()

  const items = React.useMemo(
    () => (prefillItems ?? []).map(toPOLineItem),
    [prefillItems]
  )

  const openPOs = React.useMemo(() => {
    const q = search.trim().toLowerCase()
    const matches = (p: (typeof pos)[number]) =>
      !q ||
      p.po_id.toLowerCase().includes(q) ||
      (p.vendor ?? '').toLowerCase().includes(q) ||
      (p.supplier ?? '').toLowerCase().includes(q)
    return pos
      .filter((p) => OPEN_STATUSES.includes(p.status) && matches(p))
      .slice()
      .sort((a, b) =>
        (b.created_date ?? b.updated_at ?? '').localeCompare(
          a.created_date ?? a.updated_at ?? ''
        )
      )
  }, [pos, search])

  // usePO keeps the previous PO's data around for a beat after the selection
  // changes — only trust it once it matches the selected id.
  const po = poData && poData.po_id === poId ? poData : null
  // Until the selected PO's detail lands, alreadyOn is empty and toAdd would
  // wrongly include variants the PO already has — the backend answers 409.
  const poLoaded = !!po
  const alreadyOn = React.useMemo(
    () => new Set((po?.items ?? []).map((i) => i.variant_id)),
    [po]
  )
  const toAdd = items.filter((i) => !alreadyOn.has(i.variant_id))
  const skipped = items.length - toAdd.length

  // Advisory only — a distributor PO can legitimately carry several brands, so
  // this warns without blocking.
  const brandMismatch =
    !!po &&
    !(po.supplier ?? '').trim() &&
    !!(po.vendor ?? '').trim() &&
    toAdd.some(
      (i) =>
        i.vendor.trim() !== '' &&
        i.vendor.trim().toLowerCase() !== po.vendor.trim().toLowerCase()
    )

  async function handleAdd() {
    if (!poId || !poLoaded || toAdd.length === 0) return
    try {
      await addItems.mutateAsync({ po_id: poId, items: toAdd })
      toast.success(`Added ${toAdd.length} item(s) to ${poId}`)
      onAdded?.()
      close()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add items')
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Filter by PO number, vendor or supplier…"
        className="h-8 text-xs"
        data-testid="add-to-po-search"
      />

      <div
        className="max-h-[240px] overflow-y-auto border border-[var(--line-warm)]"
        data-testid="add-to-po-list"
      >
        {isLoading ? (
          <div className="space-y-1 p-2">
            <Skeleton className="h-7 w-full" />
            <Skeleton className="h-7 w-full" />
          </div>
        ) : openPOs.length === 0 ? (
          <p className="p-3 text-xs text-[var(--mute)]">
            No open POs — create one with &apos;PO from selected&apos; instead.
          </p>
        ) : (
          openPOs.map((p) => (
            <button
              key={p.po_id}
              type="button"
              aria-pressed={p.po_id === poId}
              onClick={() => setPoId(p.po_id)}
              className="flex w-full items-center gap-2 border-b border-[var(--line-warm)] px-3 py-2 text-left text-xs last:border-b-0 hover:bg-[var(--surface-page)]"
              style={p.po_id === poId ? { background: 'var(--surface-header)' } : undefined}
            >
              <span className="shrink-0 font-semibold text-[var(--text-primary)]">{p.po_id}</span>
              <StatusBadge status={p.status} className="shrink-0" />
              <span className="min-w-0 flex-1 truncate text-[var(--ink-2)]">
                {p.supplier || p.vendor}
              </span>
              <span className="shrink-0 tabular-nums text-[var(--mute)]">
                {p.items_count} items
              </span>
              <span className="shrink-0 tabular-nums text-[var(--mute)]">
                {fmtDate(p.created_date)}
              </span>
            </button>
          ))
        )}
      </div>

      {poId && (
        <div data-testid="add-to-po-items">
          {!poLoaded ? (
            <div className="space-y-1 border-t border-[var(--line-warm)] pt-2">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-full" />
            </div>
          ) : (
            <>
              <ul className="max-h-[160px] overflow-y-auto border-t border-[var(--line-warm)]">
                {toAdd.map((i) => (
                  <li
                    key={i.variant_id}
                    className="flex items-center gap-2 border-b border-[var(--line-warm)] py-1.5 text-xs last:border-b-0"
                  >
                    <span className="min-w-0 flex-1 truncate text-[var(--text-primary)]">
                      {i.product_title}{' '}
                      <span className="text-[var(--mute)]">{i.variant_title}</span>
                    </span>
                    <span className="shrink-0 tabular-nums text-[var(--ink-2)]">
                      ×{i.qty_ordered}
                    </span>
                    <span className="shrink-0 tabular-nums text-[var(--ink-2)]">
                      {fmtCurrency(i.cost_base)}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="pt-2 text-xs text-[var(--mute)]">
                {toAdd.length} to add
                {skipped > 0 && ` · ${skipped} already on this PO (skipped)`}
              </p>
              {brandMismatch && (
                <p className="pt-1 text-xs text-[var(--status-awaiting-fg)]">
                  Some items are from a different brand than this PO ({po!.vendor}).
                </p>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={close}
          disabled={addItems.isPending}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleAdd}
          disabled={!poId || !poLoaded || toAdd.length === 0 || addItems.isPending}
          data-testid="add-to-po-confirm"
        >
          {poId
            ? `Add ${toAdd.length} item${toAdd.length === 1 ? '' : 's'} to ${poId}`
            : 'Add to PO'}
        </Button>
      </div>
    </div>
  )
}

export function AddSelectedToPOModal({ onAdded }: { onAdded?: () => void }) {
  const activeModal = useUIStore((s) => s.activeModal)
  const setActiveModal = useUIStore((s) => s.setActiveModal)
  const open = activeModal === 'addToPO'

  function close() {
    setActiveModal(null)
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Add to existing PO</DialogTitle>
          <DialogDescription>
            Adds the selected products as new line items. Only POs that
            haven&apos;t been ordered yet are listed.
          </DialogDescription>
        </DialogHeader>
        {/* Mounted only while open, so the PO list query and the local
            selection/search state start fresh on every open. */}
        {open && <AddToPOBody onAdded={onAdded} close={close} />}
      </DialogContent>
    </Dialog>
  )
}
